import Fuse from 'fuse.js';
import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import FilterableList from '../../../../common/FilterableList';
import { type ConfigOptions } from './RouterConfigBlock';
import dropdownIcon from '../../../../../../assets/icons/localization/dropdown-arrow.svg';
import premiumIcon from '../../../../../../assets/icons/devices-router/premium.svg';
import styles from './PickerItemComponent.scss';

type Options = ConfigOptions['options'];

// https://www.fusejs.io/api/options.html#threshold
const perfectMatchThreshold = 0.01;

const filterFunction = (options: Options, filter: string) => {
  const fuse = new Fuse(options, { keys: ['title', 'suboptions.title'], ignoreLocation: true, includeScore: true });

  let outerSearch = fuse.search(filter);

  if (outerSearch.some((result) => result.score && result.score <= perfectMatchThreshold)) {
    outerSearch = outerSearch.filter((result) => result.score && result.score <= perfectMatchThreshold);
  }

  return outerSearch
    .map((result) => result.item)
    .map((item) => {
      if (item.suboptions.length === 0) {
        return item;
      }

      const innerSearch = new Fuse(item.suboptions, { keys: ['title'] });

      const fileteredSubitems = innerSearch.search(filter).map((result) => result.item);

      return {
        ...item,
        /*
        if we got 0 matched subitems, but the item has matched -- it means user was searching item keyword
        in this case we show all subitems.
        For example, user has been searching for `Netherlands`, the item would match, but none of the subitems.
      */
        suboptions: fileteredSubitems.length > 0 ? fileteredSubitems : item.suboptions,
      };
    });
};

interface CountryPickerItemProps {
  item: Options[number];
  filterValue: string;
  selectedItem: {
    shortcut_group_id: string;
    shortcut_id: string;
    cluster_id: string;
  };
  onSelect: (shortcut_group_id: string, shortcut_id: string, cluster_id: string, isOptimalLocation: boolean) => void;
  openItemUUID: string | null;
  onOpen: (uuid: string | null) => void;
  hasActiveSubscription: boolean;
}

const CountryPickerItem = ({
  item,
  filterValue,
  selectedItem,
  onSelect,
  openItemUUID,
  onOpen,
  hasActiveSubscription,
}: CountryPickerItemProps) => {
  const [childrenShown, setChildrenShown] = useState(false);

  useEffect(() => {
    if (filterValue) {
      setChildrenShown(true);
    } else {
      setChildrenShown(openItemUUID === item.uuid);
    }
  }, [filterValue, openItemUUID]);

  const isSelected =
    selectedItem.shortcut_group_id === item.shortcut_group_id && selectedItem.shortcut_id === item.shortcut_id;

  const handleSelect = (clusterId: string) => {
    onSelect(item.shortcut_group_id, item.shortcut_id, clusterId, item.isOptimalLocation || false);
  };

  const handleChildrenToggle = () => {
    setChildrenShown((is) => !is);
    if (!filterValue) {
      onOpen(childrenShown ? null : item.uuid);
    }
  };

  const withPremiumIcon = item.shortcuts_access === 'SHORTCUTS_ACCESS_PREMIUM' && !hasActiveSubscription;

  // displaying only available cluster inline with the shortcut
  if (item.suboptions.length === 1) {
    return (
      <button
        type="button"
        className={styles.item}
        key={item.shortcut_id}
        onClick={() => {
          handleSelect(item.suboptions[0].cluster_id);
        }}
      >
        <div className={styles.mainLine}>
          <div className={clsx(styles.title, isSelected && styles.titleSelected)}>
            <div className={styles.imageWrapper}>
              <img className={styles.image} src={item.imageUrl} width={42} height={32} alt="" />
              {withPremiumIcon ? (
                <img className={styles.premiumIcon} src={premiumIcon} width={20} height={20} alt="" />
              ) : null}
            </div>
            {item.title}
            <span className={styles.titleOpaque}>·</span>
            <span className={styles.titleOpaque}>{item.suboptions[0].title}</span>
          </div>
        </div>
      </button>
    );
  }
  // showing dropdown for all available clusters
  if (item.suboptions.length > 1) {
    const selectedChildTitle = isSelected
      ? item.suboptions.find((i) => i.cluster_id === selectedItem.cluster_id)?.title
      : null;

    return (
      <div className={styles.item} key={item.shortcut_id}>
        <div className={styles.mainLine}>
          <div className={clsx(styles.title, isSelected && styles.titleSelected)}>
            <div className={styles.imageWrapper}>
              <img className={styles.image} src={item.imageUrl} width={42} height={32} alt="" />
              {withPremiumIcon ? (
                <img className={styles.premiumIcon} src={premiumIcon} width={20} height={20} alt="" />
              ) : null}
            </div>
            {item.title}
            <span className={styles.titleOpaque}>·</span>
            <span className={styles.titleOpaque}>
              {selectedChildTitle ? (
                <span>{selectedChildTitle}</span>
              ) : (
                <FormattedMessage id="profile.routerSection.downloadBlock.pickCityLabel" defaultMessage="Select city" />
              )}
            </span>
          </div>

          <button
            type="button"
            className={clsx(styles.button, childrenShown && styles.buttonExpanded)}
            onClick={handleChildrenToggle}
          >
            <img src={dropdownIcon} width="24" height="24" alt="" />
          </button>
        </div>
        {item.suboptions.length > 0 && childrenShown && (
          <div className={styles.children}>
            {item.suboptions.map((i) => (
              <button
                type="button"
                key={i.cluster_id}
                className={clsx(i.cluster_id === selectedItem.cluster_id && styles.childSelected)}
                onClick={() => {
                  handleSelect(i.cluster_id);
                }}
              >
                {i.title}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // if no clusters are available, this item should not be displayed
  return null;
};

interface CountryPickerListProps {
  options: Options;
  filterValue: string;
  selectedItem: {
    shortcut_group_id: string;
    shortcut_id: string;
    cluster_id: string;
  };
  onSelect: (shortcut_group_id: string, shortcut_id: string, cluster_id: string, isOptimalLocation: boolean) => void;
  notFoundMessage: string;
  hasActiveSubscription: boolean;
}

const CountryPickerList = ({
  options,
  filterValue,
  selectedItem,
  onSelect,
  notFoundMessage,
  hasActiveSubscription,
}: CountryPickerListProps) => {
  const [openItemUUID, setOpenItemUUID] = useState<string | null>(null);

  return (
    <FilterableList
      options={options}
      filterValue={filterValue}
      filterFunction={filterFunction}
      itemComponent={CountryPickerItem}
      passThroughProps={{ selectedItem, onSelect, openItemUUID, onOpen: setOpenItemUUID, hasActiveSubscription }}
      notFoundMessage={notFoundMessage}
      className={styles.list}
    />
  );
};

export default CountryPickerList;
